import {
  sliceLoadFail,
  sliceLoadStart,
  sliceSpecialLoadFail,
  sliceSpecialLoadHandle,
} from 'utils/tools/slice-actions';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';

import { createSlice } from 'utils/@reduxjs/toolkit';
import { masterDataSaga } from './saga';

export const initialState = {
  loading: false,
  error: false,
  countriesCodeList: [],
  countriesCodeLastFetch: 0,
  rolesList: [],
  rolesLastFetch: 0,
  statesList: [],
  statesLastFetch: 0,
  constituenciesList: [],
  constituenciesLastFetch: 0,
  languagesList: [],
  languagesLastFetch: 0,
  dateFormatsList: [],
  dateFormatsLastFetch: 0,
  currencyList: [],
  currencyLastFetch: 0,

  domainVerifyError: null,
  domainVerifyLoading: false,
  domainVerifyResult: null,

  generateDomainError: null,
  generateDomainLoading: false,
  generateDomainResult: null,

  manualVisible: false,
  manualType: 'info',

  issueSourcesList: [],
  issueSourcesLastFetch: 0,

  keywordList: [],
  keywordLastFetch: 0,
};

export const MasterDataActionTypes = {
  LIST_LOCALITIES: 'localitiesList',
  LIST_KEYWORDS: 'keywordList',
};

const endLoadSuccess = (state, action, listName, lastFetchName) => {
  state[listName] = action?.payload?.data;
  state[lastFetchName] = action?.payload?.time;
  state.loading = false;
};

const slice = createSlice({
  name: 'masterData',
  initialState,
  reducers: {
    toggleManualVisible(state, action) {
      state.manualVisible = action?.payload?.manualVisible;
      state.manualType = action?.payload?.manualType;
    },
    generateSubdomainStart(state) {
      state.generateDomainError = null;
      state.generateDomainLoading = true;
    },
    generateSubdomainEnd(state, action) {
      state.generateDomainLoading = false;
      state.generateDomainResult = action?.payload;
    },
    generateSubdomainError(state, action) {
      state.generateDomainError = action.payload;
      state.generateDomainLoading = false;
    },
    verifySubdomainStart(state) {
      state.domainVerifyError = null;
      state.domainVerifyLoading = true;
    },
    verifySubdomainEnd(state, action) {
      state.domainVerifyLoading = false;
      state.domainVerifyResult = action?.payload;
    },
    verifySubdomainError(state, action) {
      state.domainVerifyError = action.payload;
      state.domainVerifyLoading = false;
    },

    countriesCodeLoadStart(state) {
      sliceLoadStart(state);
    },
    countriesCodeLoadEnd(state, action) {
      endLoadSuccess(
        state,
        action,
        'countriesCodeList',
        'countriesCodeLastFetch',
      );
    },
    rolesLoadStart(state) {
      sliceLoadStart(state);
    },
    rolesLoadEnd(state, action) {
      endLoadSuccess(
        state,
        action,
        `${action?.payload?.level_type}RolesList`,
        `${action?.payload?.level_type}RolesLastFetch`,
      );
    },
    statesLoadStart(state) {
      sliceLoadStart(state);
    },
    statesLoadEnd(state, action) {
      endLoadSuccess(state, action, 'statesList', 'statesLastFetch');
    },
    constituenciesLoadStart(state) {
      sliceLoadStart(state);
    },
    constituenciesLoadEnd(state, action) {
      endLoadSuccess(
        state,
        action,
        'constituenciesList',
        'constituenciesLastFetch',
      );
    },
    dictionaryLoadStart(state) {
      sliceLoadStart(state);
    },
    secondaryDictionaryLoadStart(state) {
      sliceLoadStart(state);
    },
    dictionaryLoadEnd(state, action) {
      endLoadSuccess(
        state,
        action,
        `${action?.payload?.type}DataList`,
        `${action?.payload?.type}DataLastFetch`,
      );
    },
    languagesLoadStart(state) {
      sliceLoadStart(state);
    },
    languagesLoadEnd(state, action) {
      endLoadSuccess(state, action, 'languagesList', 'languagesLastFetch');
    },
    dateFormatsLoadStart(state) {
      sliceLoadStart(state);
    },
    dateFormatsLoadEnd(state, action) {
      endLoadSuccess(state, action, 'dateFormatsList', 'dateFormatsLastFetch');
    },
    currencyLoadStart(state) {
      sliceLoadStart(state);
    },
    currencyLoadEnd(state, action) {
      endLoadSuccess(state, action, 'currencyList', 'currencyLastFetch');
    },
    issueSourceListLoadStart(state) {
      sliceLoadStart(state);
    },
    issueSourceListLoadEnd(state, action) {
      endLoadSuccess(
        state,
        action,
        'issueSourcesList',
        'issueSourcesLastFetch',
      );
    },
    masterLoadError(state, action) {
      sliceLoadFail(state, action);
    },

    fetchLocalitiesListStart(state) {
      sliceSpecialLoadHandle(
        state,
        MasterDataActionTypes?.LIST_LOCALITIES,
        true,
      );
    },
    fetchKeywordListStart(state) {
      sliceSpecialLoadHandle(state, MasterDataActionTypes?.LIST_KEYWORDS, true);
    },
    fetchPageActionEnd(state, action) {
      const { actionFor, data, page_number, page_size, total } = action.payload;
      sliceSpecialLoadHandle(
        state,
        actionFor,
        false,
        data,
        page_number,
        page_size,
        total,
      );
    },
    fetchPageActionError(state, action) {
      const { actionFor, error } = action.payload;
      sliceSpecialLoadFail(state, actionFor, error);
    },
  },
});

export const { actions: masterDataActions } = slice;

export const useMasterDataSlice = () => {
  useInjectReducer({ key: slice.name, reducer: slice.reducer });
  useInjectSaga({ key: slice.name, saga: masterDataSaga });
  return { actions: slice.actions };
};

/**
 * Example Usage:
 *
 * export function MyComponentNeedingThisSlice() {
 *  const { actions } = useMasterDataSlice();
 *
 *  const onButtonClick = (evt) => {
 *    dispatch(actions.someAction());
 *   };
 * }
 */
